/**
 * ChecklistsView - live, per-project, shared SVM checklists (Process / Controls
 * / Install), rebuilt around the real Sealer/Process SVM template.
 *
 * This file is the ROOT only: the three-step state machine plus which
 * discipline (book) the zone step opens. Everything else lives in ./checklists:
 *   svmApi.js        - fetch helpers + every /api/svm path + template cache
 *   svmModel.js      - styles, hooks, formatters, scope + progress math
 *   SvmShared.jsx    - ErrorBanner / LoadingNote / Breadcrumb / ProgressBar / SyncedAgo
 *   ProjectPicker.jsx, DisciplinePicker.jsx, SharePanel.jsx
 *   ZoneChecklist.jsx, SubsectionBlock.jsx, ItemRow.jsx, SignCell.jsx,
 *   CellPopover.jsx, ZoneScopePanel.jsx
 *
 * Three-step flow (state machine: 'projects' | 'disciplines' | 'zone'):
 *   1. Project picker  - GET /api/svm/projects. Searchable card list of active
 *      (Won, not closed) projects; the caller's own assignments pinned on top.
 *   2. Discipline cards - GET /api/svm/disciplines?project=… Process is live
 *      (progress + open); Controls and Install are "Coming soon". Privileged
 *      roles (admin/operations/manager) also get a "Customer link" button on
 *      the breadcrumb row → SharePanel (create/copy/revoke read-only customer
 *      progress links). Engineers never see it.
 *   3. Zone view - GET /api/svm/template (fetched ONCE, cached module-level;
 *      ~340KB, never refetched on zone change) + GET /api/svm/checks per zone.
 *      Phase sections → collapsible sub-sections → item rows with per-cell
 *      sign-offs (one wide CELL cell or one square per robot R1…R6).
 *
 * Live behaviour: ticking is optimistic (fills instantly, POSTs, reverts on
 * failure); the active zone re-polls every ~12s and on window focus /
 * visibilitychange, merging server state over local state EXCEPT keys with an
 * in-flight optimistic write. A "Synced Xs ago" indicator sits in the header.
 *
 * N/A UX (chosen): clicking an EMPTY cell ticks it Done. To mark a cell N/A
 * without ticking it, each item row ends with a tiny "⋯" menu that opens the
 * same popover in N/A mode with a CELL/robot selector (required note, save
 * disabled until non-empty). A DONE cell can also be flipped to N/A from its
 * own popover. Filled-cell popover: full name, timestamp, note, Add/Edit note,
 * Mark N/A, Untick, History (event log inline).
 *
 * Zone scope: every project builds a SUBSET of the template zones with a
 * per-zone ROBOT COUNT (array of { key, robots } objects, or null = all zones
 * at full template robots; legacy plain-string entries normalize to the full
 * template count). The scope arrives on the Process discipline object
 * (/disciplines) and rides along on every /checks response, so a colleague's
 * edit propagates via the normal poll. The zone view renders EFFECTIVE zones —
 * template zones filtered to the scoped keys with each robots array sliced to
 * the scoped count - so chips, totals, the robot header/filter, the parallel
 * checks prefetch and the overall bar cover only in-scope zones and robots.
 * An "Edit zones" button in the zone-view header PUTs /api/svm/zones.
 *
 * Endpoints: /api/svm/projects, /disciplines, /template, /checks, /check
 * (POST/DELETE), /zones (PUT), /history, /share (POST/GET/DELETE - customer
 * read-only progress links, admin/operations/manager only; the tokenized URL
 * is returned ONLY by the creating POST). window.fetch only - an interceptor
 * attaches the auth token; we NEVER set Authorization ourselves.
 *
 * Desktop layout, Montserrat, inline styles - matches MyScheduleView /
 * DailyReportView conventions. All components at module level.
 *
 * Phone layout (≤640px, useIsNarrow): purely presentational branch - item rows
 * stack (full item text, then a wrapping row of self-labelled sign-off cells;
 * no robot header row), zone chips become the one horizontal scroller, the
 * header stacks breadcrumb → subtitle (2-line clamp) → progress bar → controls,
 * and CellPopover / ZoneScopePanel render as bottom sheets with a dim backdrop.
 * Desktop (≥641px) rendering is byte-for-byte the previous layout.
 */
import { useCallback, useState } from 'react';
import { DEFAULT_DISCIPLINE } from './checklists/svmModel';
import ProjectPicker from './checklists/ProjectPicker';
import DisciplinePicker from './checklists/DisciplinePicker';
import ZoneChecklist from './checklists/ZoneChecklist';

// ═══════════════════════ ROOT - the three-step state machine ═══════════════════════
export default function ChecklistsView() {
  const [step, setStep] = useState('projects'); // 'projects' | 'disciplines' | 'zone'
  const [discipline, setDiscipline] = useState(DEFAULT_DISCIPLINE); // which BOOK the zone step shows
  const [project, setProject] = useState(null);
  const [disciplineZones, setDisciplineZones] = useState(null); // Process zone scope from /disciplines ([{ key, robots }] | null = all)

  const pickProject = useCallback((p) => {
    setProject(p);
    setDiscipline(DEFAULT_DISCIPLINE);
    setDisciplineZones(null);
    setStep('disciplines');
  }, []);
  const openDiscipline = useCallback((d) => {
    setDiscipline({ id: d.id, label: d.label }); // Process and Controls are different books
    setDisciplineZones(d && Array.isArray(d.zones) ? d.zones : null);
    setStep('zone');
  }, []);
  const backToProjects = useCallback(() => {
    setProject(null);
    setStep('projects');
  }, []);
  const backToDisciplines = useCallback(() => {
    setStep('disciplines');
  }, []);

  return (
    <div style={{ fontFamily: "'Montserrat', sans-serif" }}>
      {step === 'projects' && <ProjectPicker onPick={pickProject} />}
      {step === 'disciplines' && project && <DisciplinePicker project={project} onBack={backToProjects} onOpen={openDiscipline} />}
      {step === 'zone' && project && (
        <ZoneChecklist
          key={`${project.projectLabel}|${discipline.id}`}
          project={project}
          discipline={discipline}
          initialZones={disciplineZones}
          onBackToProjects={backToProjects}
          onBackToDisciplines={backToDisciplines}
        />
      )}
    </div>
  );
}
